import { useContext, useMemo, useState } from 'react';

import { Form, Formik } from 'formik';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';

import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';

import { SubmitButton } from '../../../components/buttons';
import { TextField } from '../../../components/inputs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Defaults } from '../../../constants';
import { useGetCategoriesByPage } from '../../../lib/category/hooks';
import { useAddAdditionalParentCategory } from '../../../lib/post/hooks';
import { Category } from '../../../types/Category';

type AddParentCategoryButtonProps = {
  postId: string;
  existingParentCategoryIds?: string[];
  primaryCategoryId?: string;
};

function AddParentCategoryButton({ postId, existingParentCategoryIds = [], primaryCategoryId }: AddParentCategoryButtonProps) {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);

  const [searchCategory, setSearchCategory] = useState('');
  const debouncedSearchCategoryFn = useDebouncedCallback((value: string) => {
    setSearchCategory(value);
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const { mutateAsync: addAdditionalParentCategory, isLoading } = useAddAdditionalParentCategory();

  const { data: categories } = useGetCategoriesByPage({
    page: 0,
    sort: '',
    search: searchCategory,
  });

  const options = useMemo(
    () =>
      (categories ?? []).filter(
        (category) =>
          category.id !== primaryCategoryId &&
          !existingParentCategoryIds.includes(category.id),
      ),
    [categories, existingParentCategoryIds, primaryCategoryId],
  );

  const handleSubmit = async (values: SelectCategoryFormType) => {
    await addAdditionalParentCategory(
      { postId, parentCategoryId: values.categoryId! },
      {
        onSuccess: () => {
          handleClose();
          setAlertSnackbar({
            message: 'Additional Parent Category added',
            severity: 'success',
            vertical: 'top',
          });
        },
        onError: (error: any) => {
          setAlertSnackbar({
            message: `Error adding Additional Parent Category: ${error.message || 'Unknown error'}`,
            severity: 'error',
            vertical: 'top',
          });
        },
      },
    );
  };

  return (
    <>
      <Button variant="contained" onClick={handleOpen} type="button" disabled={!postId}>
        Add
      </Button>

      <Dialog open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <Formik
          initialValues={{
            categoryId: undefined,
          }}
          validationSchema={SelectCategorySchema}
          validateOnBlur={true}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, handleChange, handleBlur }) => (
            <Form>
              <DialogTitle id="alert-dialog-title">Select Parent Category</DialogTitle>
              <DialogContent>
                <Autocomplete<Category>
                  style={{ minWidth: '320px', paddingTop: '1rem' }}
                  options={options}
                  getOptionLabel={(option) => option.title}
                  value={options.find((c) => c.id === values.categoryId) || null}
                  onChange={(_, value) => {
                    setFieldValue('categoryId', value ? value.id : '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={touched.categoryId && !!errors.categoryId}
                      type="text"
                      name="categoryId"
                      label="Category"
                      onChange={(e) => {
                        handleChange(e);
                        debouncedSearchCategoryFn(e.target.value);
                      }}
                      onBlur={handleBlur}
                      value={values.categoryId}
                      helperText={touched.categoryId && errors.categoryId}
                    />
                  )}
                />
              </DialogContent>
              <StyledDialogActions>
                <Button disabled={isLoading} onClick={handleClose} type="button">
                  Cancel
                </Button>
                <SubmitButton loading={isLoading} disabled={isLoading} autoFocus variant="contained" type="submit">
                  Submit
                </SubmitButton>
              </StyledDialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
}

export default AddParentCategoryButton;

type SelectCategoryFormType = {
  categoryId?: string;
};

const SelectCategorySchema = Yup.object().shape({
  categoryId: Yup.string().required('Category is required'),
});

const StyledDialogActions = styled(DialogActions)({
  padding: '1rem',
});
